import { useApolloClient } from '@apollo/client';
import { Typography, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { useEffect, useState } from 'react';
import ReactDiffViewer from 'react-diff-viewer';
import { useBooleanState } from 'react-use-object-state';
import { Entity } from '../../../../types';
import { EntityRawDataLegacy } from '../../../graphql/queries';
import { JupiterTheme } from '../../../theme/types';
import JSONEditor from '../../Editors/JSONEditor';
import RawDataProgress from './RawDataProgress';
import { RawDataEntity, RawDataItemsQueryResponse } from './types';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    noRawData: {
      padding: theme.spacing(2.5),
    },
  })
);

interface Props {
  entity: Entity;
  selectedRawDataObject?: string;
  selectedRawDataVersion?: string;
  comparisonRawDataVersion?: string;
}

function displayStringFromRawData(rawData: RawDataEntity): string {
  if (rawData.contentType.indexOf('text/') >= 0 && rawData.TextData) {
    return rawData.TextData;
  } else {
    return JSON.stringify(rawData, null, 2);
  }
}

const RawDataLoader = ({
  entity,
  selectedRawDataObject,
  selectedRawDataVersion,
  comparisonRawDataVersion,
}: Props) => {
  const client = useApolloClient();
  const classes = useStyles();
  const diffSplitView = useMediaQuery((theme: JupiterTheme) =>
    theme.breakpoints.up('lg')
  );

  const loadingRawData = useBooleanState(false);
  const [rawData, setRawData] = useState<RawDataEntity | null>(null);

  const loadingComparisonRawData = useBooleanState(false);
  const [
    comparisonRawData,
    setComparisonRawData,
  ] = useState<RawDataEntity | null>(null);

  useEffect(() => {
    const fetchRawDataForVersion = async () => {
      loadingRawData.setTrue();

      const rawDataQueryResult = await client.query({
        context: {
          mute: true,
        },
        query: EntityRawDataLegacy,
        variables: {
          entityId: entity.properties._id,
          source:
            entity.properties._integrationInstanceId ||
            entity.properties._source ||
            'api',
          name: selectedRawDataObject,
          versionId: selectedRawDataVersion,
        },
        fetchPolicy: 'cache-first',
      });

      const entityRawDataLegacy = rawDataQueryResult.data
        .entityRawDataLegacy as RawDataItemsQueryResponse;
      if (entityRawDataLegacy?.payload?.length > 0) {
        // Set rawData to the first raw data object in the payload, which will
        // also be the only raw data object in the payload since we specified a name
        // in the raw data query.
        setRawData(entityRawDataLegacy.payload[0]);
      }

      loadingRawData.setFalse();
    };

    if (selectedRawDataObject && selectedRawDataVersion) {
      void fetchRawDataForVersion();
    }
  }, [entity, selectedRawDataObject, selectedRawDataVersion]);

  useEffect(() => {
    const fetchComparisonRawData = async () => {
      loadingComparisonRawData.setTrue();

      const comparisonQueryResult = await client.query({
        context: {
          mute: true,
        },
        query: EntityRawDataLegacy,
        variables: {
          entityId: entity.properties._id,
          source:
            entity.properties._integrationInstanceId ||
            entity.properties._source,
          name: selectedRawDataObject,
          versionId: comparisonRawDataVersion,
        },
        fetchPolicy: 'cache-first',
      });

      const comparisonRawData = comparisonQueryResult.data
        .entityRawDataLegacy as RawDataItemsQueryResponse;
      if (comparisonRawData) {
        // Set comparisonRawData to the first raw data object in the payload,
        // which will also be the only raw data object in the payload since we
        // specified a name in the raw data query.
        setComparisonRawData(comparisonRawData.payload[0]);
      }

      loadingComparisonRawData.setFalse();
    };

    if (selectedRawDataObject && comparisonRawDataVersion) {
      void fetchComparisonRawData();
    } else {
      setComparisonRawData(null);
    }
  }, [entity, selectedRawDataObject, comparisonRawDataVersion]);

  if (loadingRawData.state || loadingComparisonRawData.state) {
    return <RawDataProgress />;
  }

  if (!rawData) {
    return (
      <Typography key="noRawData" variant="body1" className={classes.noRawData}>
        No Raw Data for Entity
      </Typography>
    );
  }

  const rawDataForDisplay = displayStringFromRawData(rawData);

  if (
    rawData &&
    comparisonRawData &&
    rawData.name === comparisonRawData.name &&
    selectedRawDataVersion !== comparisonRawDataVersion
  ) {
    const comparisonRawDataForDisplay = displayStringFromRawData(
      comparisonRawData
    );

    return (
      <ReactDiffViewer
        oldValue={rawDataForDisplay}
        newValue={comparisonRawDataForDisplay}
        splitView={diffSplitView}
      />
    );
  }

  return (
    <JSONEditor
      key="rawDataEditor"
      readOnly={true}
      wrap={true}
      value={rawDataForDisplay}
    />
  );
};
export default RawDataLoader;
